import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { matchPath } from 'react-router-dom';
import { alpha, Box, Menu, MenuItem, Stack, Typography, useTheme } from '@mui/material';
import { createGlassmorphismStyles } from 'src/components/GlassmorphismStyles';
import { useSettingsContext } from 'src/components/settings';

import { NavItemProps, NavProps } from '../types';

export default function NavDesktop({ data }: NavProps) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { themeMode } = useSettingsContext();
  const theme = useTheme();

  const fill = {
    default: themeMode === 'dark' ? '#919EAB' : '#999999',
    active: themeMode === 'dark' ? theme.palette.primary.light : theme.palette.primary.main,
  };

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  // 물방울 상태 관리
  const [mainBubble, setMainBubble] = useState({ left: 0, width: 0, show: false });
  const [subBubble, setSubBubble] = useState({ top: 0, height: 0, show: false });

  const mainMenuRefs = useRef<(HTMLElement | null)[]>([]);
  const subMenuRefs = useRef<(HTMLElement | null)[]>([]);
  const mainContainerRef = useRef<HTMLElement | null>(null);
  const subContainerRef = useRef<HTMLUListElement | null>(null);
  const closeTimerRef = useRef<NodeJS.Timeout | null>(null);

  const clearTimer = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const closeMenu = useCallback(() => {
    clearTimer();
    setAnchorEl(null);
    setOpenMenuId(null);
    setSubBubble({ top: 0, height: 0, show: false });
  }, [clearTimer]);

  const openMenu = useCallback(
    (element: HTMLElement, menuId: string) => {
      clearTimer();
      setAnchorEl(element);
      setOpenMenuId(menuId);
    },
    [clearTimer],
  );

  // 메인 물방울 업데이트
  const updateMainBubble = useCallback((index: number) => {
    if (!mainContainerRef.current || !mainMenuRefs.current[index]) return;

    const container = mainContainerRef.current;
    const menuItem = mainMenuRefs.current[index];

    if (!menuItem) return;

    const containerRect = container.getBoundingClientRect();
    const menuRect = menuItem.getBoundingClientRect();

    setMainBubble({
      left: menuRect.left - containerRect.left,
      width: menuRect.width,
      show: true,
    });
  }, []);

  // 서브 물방울 업데이트
  const updateSubBubble = useCallback((index: number) => {
    if (!subContainerRef.current || !subMenuRefs.current[index]) return;

    const container = subContainerRef.current;
    const menuItem = subMenuRefs.current[index];

    if (!menuItem) return;

    const containerRect = container.getBoundingClientRect();
    const menuRect = menuItem.getBoundingClientRect();

    setSubBubble({
      top: menuRect.top - containerRect.top,
      height: menuRect.height,
      show: true,
    });
  }, []);

  const handleParentMouseEnter = useCallback(
    (e: React.MouseEvent<HTMLElement>, item: NavItemProps, index: number) => {
      clearTimer();
      if (item.children) {
        if (openMenuId && openMenuId !== item.path) {
          setAnchorEl(null);
          setOpenMenuId(null);
          setSubBubble({ top: 0, height: 0, show: false });
          // 약간의 딜레이 후 새 메뉴 열기
          setTimeout(() => {
            updateMainBubble(index);
            openMenu(e.currentTarget, item.path);
          }, 0);
        } else {
          updateMainBubble(index);
          setSubBubble({ top: 0, height: 0, show: false });
          openMenu(e.currentTarget, item.path);
        }
      } else {
        // 하위 메뉴 없으면
        updateMainBubble(index);
        setSubBubble({ top: 0, height: 0, show: false });
        closeMenu();
      }
    },
    [openMenuId, openMenu, closeMenu, clearTimer, updateMainBubble],
  );

  // 전체 메뉴 영역(상위+하위) 이탈
  const handleMouseLeaveMenu = useCallback(() => {
    clearTimer();
    closeTimerRef.current = setTimeout(() => {
      closeMenu();
      // 선택된 메뉴로 물방울 복귀
      const selectedIndex = data.findIndex((item: NavItemProps) =>
        item.children
          ? Boolean(
              item.children.some((child) =>
                Boolean(matchPath({ path: child.path, end: false }, pathname)),
              ),
            )
          : (pathname === '/' && item.path === '/monthly-report') ||
            Boolean(matchPath({ path: item.path, end: true }, pathname)),
      );

      if (selectedIndex !== -1) {
        updateMainBubble(selectedIndex);
      } else {
        setMainBubble((prev) => ({ ...prev, show: false }));
      }
    }, 200);
  }, [clearTimer, closeMenu, data, pathname, updateMainBubble]);

  // 하위 메뉴 진입 시 닫기 타이머 취소
  const handleSubmenuMouseEnter = useCallback(() => {
    clearTimer();
  }, [clearTimer]);

  // 하위 메뉴 이탈
  const handleSubmenuMouseLeave = useCallback(() => {
    clearTimer();
    closeTimerRef.current = setTimeout(() => {
      closeMenu();
      // 선택된 서브메뉴로 물방울 복귀
      const activeMenuItem = data.find((item: NavItemProps) =>
        item.children?.some((child) =>
          Boolean(matchPath({ path: child.path, end: false }, pathname)),
        ),
      );
      if (activeMenuItem && activeMenuItem.children) {
        const selectedChildIndex = activeMenuItem.children.findIndex((child) =>
          Boolean(matchPath({ path: child.path, end: false }, pathname)),
        );
        if (selectedChildIndex !== -1) {
          updateSubBubble(selectedChildIndex);
          return;
        }
      }
      setSubBubble((prev) => ({ ...prev, show: false }));
    }, 200);
  }, [clearTimer, closeMenu, data, pathname, updateSubBubble]);

  const handleClick = useCallback(
    (item: NavItemProps) => {
      if (!item.children && !pathname.includes(item.path)) {
        closeMenu();
        navigate(item.path);
      }
    },
    [pathname, navigate, closeMenu],
  );

  useEffect(() => {
    return () => clearTimer();
  }, [clearTimer]);

  useEffect(() => {
    const selectedIndex = data.findIndex(
      (item: NavItemProps) =>
        (pathname === '/' && item.path === '/monthly-report') ||
        Boolean(matchPath({ path: item.path, end: true }, pathname)) ||
        Boolean(
          item.children?.some((child) =>
            Boolean(matchPath({ path: child.path, end: false }, pathname)),
          ),
        ),
    );
    if (selectedIndex !== -1) {
      setTimeout(() => {
        updateMainBubble(selectedIndex);
      }, 100);
    } else {
      // 추가: 메뉴에 없는 경로면 물방울 숨기기
      setMainBubble({ left: 0, width: 0, show: false });
    }
  }, [pathname, data, updateMainBubble]);

  return (
    <Stack component="nav" direction="row">
      <Stack
        ref={mainContainerRef}
        direction={'row'}
        spacing={4}
        sx={{
          '& *': { transition: 'all 0.2s' },
          position: 'relative',
        }}
        onMouseLeave={handleMouseLeaveMenu}
      >
        {/* 물방울 효과 */}
        <Box
          sx={{
            top: 0,
            bottom: 0,
            left: `${mainBubble.left}px`,
            width: `${mainBubble.width}px`,
            borderRadius: '999px',
            opacity: mainBubble.show ? 1 : 0,
            ...createGlassmorphismStyles(theme, {
              intensity: 'light',
              elevation: 'low',
            }),
            position: 'absolute',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '4px',
              left: '12px',
              right: '12px',
              height: '16px',
              borderRadius: '8px',
              opacity: 0.5,
              background: `linear-gradient(to bottom, ${alpha(
                theme.palette.common.white,
                0.5,
              )}, transparent)`,
              filter: 'blur(6px)',
            },
          }}
        />

        {data.map((item: NavItemProps, index: number) => {
          const isChildActive = item.children?.some((child) =>
            Boolean(matchPath({ path: child.path, end: false }, pathname)),
          );
          const isActive =
            (pathname === '/' && item.path === '/monthly-report') ||
            Boolean(matchPath({ path: item.path, end: true }, pathname)) ||
            Boolean(isChildActive);
          const isOpen = openMenuId === item.path;
          const hasChildren = Boolean(item.children);

          return (
            <Box
              key={`menu-${index}`}
              ref={(el: HTMLElement | null) => {
                mainMenuRefs.current[index] = el;
              }}
              sx={{ position: 'relative' }}
              onMouseEnter={(e) => handleParentMouseEnter(e, item, index)}
              onMouseLeave={handleMouseLeaveMenu}
            >
              <Box
                component={'nav'}
                sx={{
                  cursor: 'pointer',
                  gap: 1,
                  padding: '8px 16px',
                  borderRadius: '999px',
                }}
                onClick={() => handleClick(item)}
              >
                <Typography
                  variant={'body1'}
                  component={'span'}
                  sx={{
                    color: isActive || isChildActive ? fill.active : fill.default,
                    fontWeight: isActive || isChildActive ? 700 : 400,
                    wordBreak: 'keep-all',
                  }}
                >
                  {item.title}
                </Typography>
              </Box>
              {hasChildren && openMenuId === item.path && (
                <Menu
                  open={isOpen && anchorEl !== null}
                  anchorEl={anchorEl}
                  onClose={closeMenu}
                  hideBackdrop
                  disableAutoFocusItem
                  disableRestoreFocus
                  disableScrollLock
                  slotProps={{
                    root: { style: { pointerEvents: 'none' } },
                  }}
                  PaperProps={{
                    elevation: 0,
                    onMouseEnter: handleSubmenuMouseEnter,
                    onMouseLeave: handleSubmenuMouseLeave,
                    sx: {
                      pointerEvents: 'auto',
                      width: 'fit-content',
                      mt: 0.5,
                      px: 1,
                      '& .MuiMenuItem-root': {
                        '&:hover': { backgroundColor: 'transparent' },
                      },
                    },
                  }}
                  transformOrigin={{ horizontal: 'left', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
                  MenuListProps={{
                    ref: subContainerRef,
                    sx: { py: 1 },
                  }}
                >
                  {/* 서브메뉴 물방울 */}
                  <Box
                    sx={{
                      top: `${subBubble.top}px`,
                      height: `${subBubble.height}px`,
                      left: 0,
                      right: 0,
                      borderRadius: '999px',
                      opacity: subBubble.show ? 1 : 0,
                      ...createGlassmorphismStyles(theme, {
                        intensity: 'light',
                        elevation: 'low',
                      }),
                      position: 'absolute',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: '4px',
                        left: '12px',
                        right: '12px',
                        height: '16px',
                        borderRadius: '8px',
                        opacity: 0.5,
                        background: `linear-gradient(to bottom, ${alpha(
                          theme.palette.common.white,
                          0.5,
                        )}, transparent)`,
                        filter: 'blur(6px)',
                      },
                    }}
                  />

                  {item.children?.map((child, j) => {
                    const isChildSelected = Boolean(
                      matchPath({ path: child.path, end: false }, pathname),
                    );

                    return (
                      <MenuItem
                        key={'child-' + j}
                        ref={(el: HTMLLIElement | null) => {
                          subMenuRefs.current[j] = el;
                        }}
                        onMouseEnter={() => updateSubBubble(j)}
                        onClick={() => {
                          navigate(child.path);
                          closeMenu();
                        }}
                        sx={{
                          mx: 1,
                          borderRadius: '8px',
                          transition: 'all 0.2s',
                          position: 'relative',
                          zIndex: 1,
                        }}
                      >
                        <Typography
                          sx={{
                            color: isChildSelected ? fill.active : fill.default,
                            fontWeight: isChildSelected ? 700 : 400,
                            wordBreak: 'keep-all',
                            fontSize: '0.9rem',
                          }}
                        >
                          {child.title}
                        </Typography>
                      </MenuItem>
                    );
                  })}
                </Menu>
              )}
            </Box>
          );
        })}
      </Stack>
    </Stack>
  );
}
